import { isUndefined } from '@src/utils/type';
import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { useAccount } from '../auth-context';

type PermissionProviderProps = {
  children: ReactNode;
};

export type PermissionName =
  | 'DashboardViewValueVolume'
  | 'DashboardViewRevenue'
  | 'DashboardViewSuccessRate'
  | 'DashboardRAASViewValueVolume'
  | 'SenderView'
  | 'SenderDisable'
  | 'SenderDownload'
  | 'RecipientView'
  | 'RecipientDisable'
  | 'RecipientDownload'
  | 'RateAndFeeView'
  | 'RateAndFeeCreate'
  | 'RateAndFeeEdit'
  | 'RateAndFeeDisable'
  | 'RateAndFeeDownload'
  | 'RateAndFeeDelete'
  | 'CodeView'
  | 'CodeCreate'
  | 'CodeEdit'
  | 'CodeDisable'
  | 'CodeDownload'
  | 'CodeDelete'
  | 'TransactionView'
  | 'TransactionDownload'
  | 'BusinessView'
  | 'BusinessDownload'
  | 'BusinessDisable'
  | 'BusinessViewKYCInfo'
  | 'BusinessVerify'
  | 'BusinessDeny'
  | 'BusinessUserView'
  | 'BusinessUserDisable'
  | 'BusinessUserDownload'
  | 'BusinessTransactionView'
  | 'BusinessTransactionDownload'
  | 'BusinessRecipientView'
  | 'BusinessRecipientDisable'
  | 'BusinessRecipientDownload'
  | 'BusinessCustomerView'
  | 'BusinessCustomerDisable'
  | 'BusinessCustomerDownload'
  | 'AgentView'
  | 'AgentDownload'
  | 'AgentViewKYBInfo'
  | 'AgentVerify'
  | 'AgentDeny'
  | 'AgentDisable'
  | 'AgentCustomerView'
  | 'AgentCustomerDisable'
  | 'AgentCustomerDownload'
  | 'AgentTransactionView'
  | 'AgentTransactionDownload'
  | 'ActivityLogView'
  | 'ActivityLogDownload'
  | 'AdminView'
  | 'AdminCreate'
  | 'AdminChangeRole'
  | 'AdminDisable'
  | 'AdminDownload'
  | 'AdminDelete'
  | 'PermissionView'
  | 'PermissionDownload'
  | 'ActivityLogView'
  | 'ActivityLogDownload';

type PermissionContextType = {
  hasPermission: (permissionName: PermissionName) => boolean;
};

const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

const PermissionProvider = ({ children }: PermissionProviderProps) => {
  const { user } = useAccount();

  const userPermissions = useMemo(() => {
    return user?.RolePermissions || [];
  }, [user?.RolePermissions]);

  const hasPermission = (permissionName: PermissionName) => {
    if (!userPermissions) return false;

    for (const section of userPermissions) {
      const permission = section.Permissions.find((p) => p.Name === permissionName);
      if (permission) {
        return permission.HasPermission;
      }
    }
    return false;
  };

  const values: PermissionContextType = {
    hasPermission
  };

  return <PermissionContext.Provider value={values}>{children}</PermissionContext.Provider>;
};

export default PermissionProvider;

export const usePermission = () => {
  const context = useContext(PermissionContext);
  if (isUndefined(context)) {
    throw new Error(`usePermission must be used within a PermissionProvider`);
  }
  return context;
};
